<script>
import { mapActions, mapState } from 'pinia';

import { useUserStore } from '../../../store/useUserStore.js';
import { useHeaderStore } from '../../../store/useHeaderStore.js';
import { useCampaignStore } from '../../../store/useCampaignStore.js';
import { useNotificationStore } from '../../../store/useNotificationStore.js';

export default {
  name: 'CpHeader',
  mixins: [require('../mixins/breakpoints').default],
  components: {
    CpBaseSvgIcon: require('../base-svg-icon').default,
    CpButtonLink: require('../button-link').default,
    CpButton: require('../button').default,
    CpHeaderMenuTooltip: require('./header-menu-tooltip').default,
    CpSiteSearch: require('./search').default,
    CpSiteClosureBanner: require('./site-closure-banner').default,
    CpSiteChatNotification: require('./chat-notification').default,
    CpCommonUseDialog: require('../dialogs/CommonUse').default,
    CpSidebarMenuGlobal: require('../sidebar-menu-global').default,
    CpDropdownMenu: require('../social-feed/DropdownMenu').default,
    CpInviteMembersDialog: require('../dialogs/invite-members').default,
    CpGoogleOneTap: require('@resources/js/auth-form/GoogleOneTap.vue').default,
  },
  directives: {
    margin: require('../directives/margins').margin,
    marginTop: require('../directives/margins').marginTop,
    marginBottom: require('../directives/margins').marginBottom,
    marginRight: require('../directives/margins').marginRight,
    marginLeft: require('../directives/margins').marginLeft,
  },
  props: {
    sitewideContent: Object,
  },
  data() {
    return {
      appUrl: process.env.MIX_APP_URL,
      path: window.location.pathname,
      urlHash: window.location.hash.replace('#', ''),
      isHowItWorksPage: ['/how-it-works', '/'].includes(this.path),
      sitewideCTA: this.sitewideContent['text'] ? this.sitewideContent['text'] : null,
      sitewideCTALink: this.sitewideContent['link'] ? this.sitewideContent['link'] : null,
      iosApp: process.env.MIX_IOS_STORE_URL,
      androidApp: process.env.MIX_ANDROID_STORE_URL,
      startItemsBase: [
        {
          url: '/user/campaigns/create',
          icon: 'fa-podium',
          title: 'Campaign',
          description: 'Raise money for your candidancy, another candidate, or for a cause.',
        },
        { url: '/blogs/create', icon: 'fa-edit', title: 'Blog Post', description: 'Write an article about political issues that are important to you.' },
        {
          url: '/petitions/create',
          icon: 'fa-file-signature',
          title: 'Petition',
          description: 'Collect signatures from supporters and send final tally directly to politicians',
        },
        {
          url: '/contact-representatives',
          icon: 'fa-landmark',
          title: 'Contact Reps',
          description: 'Endorse or oppose a representative by zip code to help spark local change.',
        },
        {
          url: '/user/rallies',
          icon: 'fa-video',
          title: 'Virtual Rally',
          description: 'Hold a live QA for your constituents or supporters and collect donations.',
        },
        {
          url: '/livestreams/start',
          icon: 'fa-signal-stream',
          title: 'Livestreams',
          description: 'Start a live conversation to share your thoughts with the community.',
        },
        { url: '/polls/create', icon: 'fa-poll', title: 'Poll', description: 'Find out what users think and make your own statistics.' },
        {
          url: '/groups/create',
          icon: 'fa-user-friends',
          title: 'Group',
          description: 'Read news, lobby political candidates, and connect with related members.',
        },
        {
          url: '/events/create',
          icon: 'fa-calendar-day',
          title: 'Event',
          description: 'Organize an event to get together with your group and build a strong community.',
        },
      ],
      isOpenhamburguerMenu: false,
      isOpenCommonUseDialog: false,
      hasEnteredAfter24HoursLoguedOut: false,
      showGoogleOneTap: false,
      showCampaignBanner: true,
      isCampaignsDropdownOpen: false,
    };
  },
  mounted() {
    const cookiePrefsKey = 'crowdblue_cookie_preferences';
    if (localStorage.getItem(cookiePrefsKey)) {
      localStorage.removeItem(cookiePrefsKey);
    }

    const storedTimestampLoguedOut = localStorage.getItem('saveTimestampAfter24HoursLoguedOut');
    const isRegularUser = localStorage.getItem('isRegularUser');

    if (storedTimestampLoguedOut) {
      const hoursDifference = (Date.now() - parseInt(storedTimestampLoguedOut)) / (1000 * 60 * 60);
      if (hoursDifference > 24) {
        this.hasEnteredAfter24HoursLoguedOut = true;
      }
    }

    if (!storedTimestampLoguedOut && !this.isUserLogin) {
      localStorage.setItem('saveTimestampAfter24HoursLoguedOut', Date.now().toString());
    }

    if (!isRegularUser && !this.isUserLogin) {
      this.isOpenCommonUseDialog = true;
      localStorage.setItem('isRegularUser', true);
    }

    if (this.hasEnteredAfter24HoursLoguedOut && !this.isUserLogin) {
      this.isOpenCommonUseDialog = true;
      localStorage.setItem('saveTimestampAfter24HoursLoguedOut', Date.now().toString());
    }

    // Show Google One Tap for non-logged-in users
    if (!this.isUserLogin) {
      const justLoggedOut = localStorage.getItem('justLoggedOut');

      if (justLoggedOut) {
        localStorage.removeItem('justLoggedOut');
        this.showGoogleOneTap = false;
      } else if (this.path.startsWith('/user/login')) {
        this.showGoogleOneTap = true;
      } else {
        const lastShown = localStorage.getItem('googleOneTapLastShown');
        const currentTime = Date.now();

        if (!lastShown) {
          this.showGoogleOneTap = true;
        } else {
          const hoursSinceLastShown = (currentTime - parseInt(lastShown)) / (1000 * 60 * 60);

          if (hoursSinceLastShown >= 24) {
            this.showGoogleOneTap = true;
          }
        }
      }
    }

    if (this.isUserLogin) {
      this.getUserCampaigns();
    }

    if (this.isCampaignEditor) {
      window.addEventListener('hashchange', this.handleHashChange);
    }
  },
  beforeDestroy() {
    if (this.isCampaignEditor) {
      window.removeEventListener('hashchange', this.handleHashChange);
    }
  },
  computed: {
    ...mapState(useUserStore, ['isUserLogin', 'isUserLoginLoading', 'userLoginData']),
    ...mapState(useHeaderStore, ['sitewideBannerInfo']),
    ...mapState(useCampaignStore, ['isCampaignSection', 'userHasCreatedCampaigns', 'userCampaignList']),
    ...mapState(useNotificationStore, ['unreadNotifications']),
    showBanner() {
      return this.sitewideCTA && this.sitewideCTALink && !this.path.startsWith('/contribute') && !this.path.startsWith('/campaigns');
    },
    isCampaignEditor() {
      return this.path.includes('/user/campaigns/') && this.path.endsWith('/editor');
    },
    isCampaignDetail() {
      return /^\/campaigns\/\d+/.test(this.path);
    },
    showCampaignEditorStyleHeader() {
      return this.isCampaignEditor || (this.isCampaignDetail && this.isMobile);
    },
    compactHeaderBreadcrumbSection() {
      if (this.isCampaignEditor) {
        return this.campaignEditorSection;
      }
      return 'CAMPAIGN DETAILS';
    },
    campaignEditorSection() {
      if (!this.isCampaignEditor) return null;
      const hash = this.urlHash.split('?')[0];
      const hashToSection = {
        '': 'THE BASICS',
        recipients: 'THE BASICS',
        'new-candidate': 'THE BASICS',
        'new-candidate-race': 'THE BASICS',
        'new-organization': 'THE BASICS',
        declaration: 'THE BASICS',
        'self-candidate': 'THE BASICS',
        race: 'THE BASICS',
        'add-own-race': 'THE BASICS',
        'add-race': 'THE BASICS',
        'candidate-declaration': 'THE BASICS',
        interests: 'THE BASICS',
        'race/ever-run': 'THE BASICS',
        'race/any-involvement': 'THE BASICS',
        'race/confirmed': 'THE BASICS',
        story: 'THE CAMPAIGN',
        goal: 'THE CAMPAIGN',
        privacy: 'THE CAMPAIGN',
        'website-links': 'THE CAMPAIGN',
        'custom-sharing': 'THE CAMPAIGN',
        optional: 'THE CAMPAIGN',
        'campaign-marketing': 'THE CAMPAIGN',
        'campaign-marketing-lists': 'THE CAMPAIGN',
        'campaign-marketing-lists-form': 'THE CAMPAIGN',
        publish: 'REVIEW',
      };
      return hashToSection[hash] || 'THE BASICS';
    },
    campaignStartItemUrl() {
      return this.userHasCreatedCampaigns ? '/user/campaigns/' : '/user/campaigns/create';
    },
    startItems() {
      return this.startItemsBase.map((item, index) =>
        index === 0 ? { ...item, url: this.campaignStartItemUrl } : item
      );
    },
  },
  methods: {
    handleLogout() {
      localStorage.removeItem('userLoginData');
      sessionStorage.removeItem('userPlanStorage');
      localStorage.setItem('isRegularUser', true);
      localStorage.setItem('saveTimestampAfter24HoursLoguedOut', Date.now().toString());
      localStorage.setItem('justLoggedOut', true);
    },
    handleHamburguerMenu() {
      this.isOpenhamburguerMenu = !this.isOpenhamburguerMenu;
    },
    handleCommonUseDialog() {
      this.isOpenCommonUseDialog = !this.isOpenCommonUseDialog;
    },
    handleRedirectDialog() {
      this.isOpenRedirectDialog = !this.isOpenRedirectDialog;
    },
    navigateTo(path) {
      window.location.href = path;
    },
    handleCloseCampaignBanner() {
      this.showCampaignBanner = false;
    },
    recalculateBannerHeight() {
      let baseHeight = 0;

      if (this.showBanner) {
        const bannerElement = document.getElementById('banner');
        if (bannerElement) {
          baseHeight = bannerElement.offsetHeight;
        }
      }

      if (this.isCampaignSection && this.showCampaignBanner) {
        const campaignBannerElement = document.getElementById('campaignBanner');
        if (campaignBannerElement) {
          baseHeight += campaignBannerElement.offsetHeight;
        }
      }

      this.setSitewideBannerInfo(baseHeight);
    },
    toggleCampaignsDropdown() {
      this.isCampaignsDropdownOpen = !this.isCampaignsDropdownOpen;
    },
    handleHashChange() {
      this.urlHash = window.location.hash.replace('#', '');
    },
    ...mapActions(useHeaderStore, ['setSitewideBannerInfo']),
    ...mapActions(useCampaignStore, ['getCreatedCampaigns', 'getUserCampaigns']),
  },
  watch: {
    isUserLogin(newVal) {
      if (newVal) {
        this.getUserCampaigns();
      }
    },
    showCampaignBanner() {
      this.$nextTick(() => {
        this.recalculateBannerHeight();
      });
    },
  },
  updated() {
    this.recalculateBannerHeight();
  },
};
</script>

<template>
  <div v-if="!isUserLoginLoading">
    <!-- Use inline styles so Vue and non - Vue play nice -->
    <!-- TO DO: uncomment when the team decides to display this modal again -->
    <!-- <CpCommonUseDialog v-if="isOpenCommonUseDialog && !isHowItWorksPage" :handleDialog="handleCommonUseDialog" /> -->
    <a v-if="showBanner" id="banner" target="_blank" class="underline-hover banner" :href="sitewideCTALink">{{ sitewideCTA }}</a>
    <div class="site-header">
      <header v-if="showCampaignEditorStyleHeader" id="js-site-header" class="site-header__main site-header__campaign-editor">
        <a href="/feed" class="site-header__campaign-editor-back">
          <i class="fal fa-arrow-left white-color fa-1-5x"></i>
        </a>
        <a href="/feed" class="site-header__main-nav-item site-header__main-nav-logo hidden-small-mobile">
          <img src="https://assets.crowdblue.com/v2/production/all/img/crowdblue-logo-white.png" class="site-header__logo" alt="CrowdBlue logo" :width="isMobile ? '150' : '209'" />
        </a>
        <span class="body white-color">CAMPAIGNS/{{ compactHeaderBreadcrumbSection }}</span>
      </header>

      <header v-else id="js-site-header" class="site-header__main">
        <!-- mobile menu toggle hamburger -->
        <div id="js-mobile-menu-toggle" class="visible-mobile menu-toggle-container">
          <i class="fal fa-bars fa-1-5x primary-1-color mobile-menu-toggle" @click="handleHamburguerMenu"></i>
        </div>

        <div class="site-header__main-nav-spacer visible-mobile"></div>

        <a v-if="!isOpenhamburguerMenu" href="/feed" class="site-header__main-nav-item site-header__main-nav-logo">
          <img src="https://assets.crowdblue.com/v2/production/all/img/crowdblue-logo-white.png" class="site-header__logo" alt="CrowdBlue logo" :width="isMobile ? '124' : '188'" />
        </a>

        <!-- Navigation Items -->
        <a href="/news" class="site-header__main-nav-item site-header__nav-link">
          <i class="fal fa-rss-square white-color"></i>
          <span class="site-header__nav-text">News</span>
        </a>
        <a href="/campaigns" class="site-header__main-nav-item site-header__nav-link">
          <i class="fal fa-podium white-color"></i>
          <span class="site-header__nav-text">Campaigns</span>
        </a>
        <a href="/community" class="site-header__main-nav-item site-header__nav-link">
          <i class="fal fa-people-arrows white-color"></i>
          <span class="site-header__nav-text">Community</span>
        </a>
        <!-- <a class="site-home-icon {{ Request::is('feed') || ( Request::url() == url('/') && user != null  ) ? 'active' : '' }}" href="{{route('social-feed.index')}}">
          <svg style="padding-top:7px;" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" xml:space="preserve" version="1.1" style="shape-rendering:geometricPrecision;text-rendering:geometricPrecision;image-rendering:optimizeQuality;" viewBox="0 0 407 488.75" x="0px" y="0px" fill-rule="evenodd" clip-rule="evenodd">
            <defs>
              <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style="stop-color:#f83600;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#fe8c00;stop-opacity:1" />
              </linearGradient>
            </defs>
            <g>
              <polygon fill="url(#grad1)" points="170,291 170,391 66,391 66,217 0,217 203,0 407,217 340,217 340,391 237,391 237,291 "/>
            </g>
          </svg>
        </a> -->

        <div class="site-header__main-nav-spacer"></div>

        <div class="site-header__button-group">
          <div class="site-header__main-nav-item site-header__main-nav-item--icon-only">
            <div v-if="isUserLogin" class="dropdown-tooltip-menu">
              <i class="fas fa-plus-circle primary-1-color fa-1-5x plus-icon"></i>
              <cp-header-menu-tooltip class="start-items" :items="startItems" title="Start" />
            </div>
            <!-- <div class="hidden-mobile">
              <a
                class="btn btn-gradient btn-large"
                href="{{route('user.login', 'create')}}"
              >
                <strong>Join Now</strong><span class="site-header__hidden-when-narrower"></span>
              </a>
            </div> -->
            <a v-else href="/campaigns/learn" class="start-campaign-container">
              <i class="fas fa-info-circle primary-1-color fa-1-5x"></i>
              <span class="hide-start-campaign subhead primary-1-color">Start Your Campaign</span>
            </a>
          </div>

          <div v-if="isUserLogin">
            <a
              href="/user/notifications"
              class="site-header__main-nav-item site-header__main-nav-item--icon-only site-header__notifications-icon-container mp-link no-underline"
              data-mp-action="click-notif-icon.coms"
            >
              <i
                class="js-sitewide-replace-attr-user-notifications-count fas fa-bell fa-1-5x primary-1-color site-header__notifications-icon"
                data-user-notifications-count="0"
              ></i>
              <span
                class="js-sitewide-replace-user-notifications-count js-sitewide-replace-attr-user-notifications-count site-header__notifications-count"
                :data-user-notifications-count="unreadNotifications"
                >{{ unreadNotifications }}</span
              >
            </a>
          </div>

          <cp-site-chat-notification v-if="isUserLogin" url="/user/chats" />

          <div v-if="path !== '/user/login'" id="js-btn-site-search-container">
            <cp-site-search-toggle-button class="inline-button site-header__main-nav-item site-header__main-nav-item--icon-only">
              <i class="fas fa-search fa-1-5x primary-1-color" title="Site search"></i>
            </cp-site-search-toggle-button>
          </div>

          <div v-if="!isUserLogin" style="display: flex; align-items: center">
            <div class="site-header__main-nav-item site-header__main-nav-item--icon-only sign-up-btns-container">
              <a href="/user/login?create" class="btn btn-primary sign-up-btn">
                <span class="subhead primary-color">Sign up</span>
              </a>
              <a href="/user/login" class="btn btn-primary log-in-btn">
                <span class="subhead primary-1-color">Log in</span>
              </a>
            </div>
            <a href="/user/login" class="user-icon-container">
              <i class="fal fa-user"></i>
            </a>
          </div>

          <div v-if="isUserLogin" class="dropdown">
            <a class="site-header__main-nav-item site-header__main-nav-item--icon-only site-header__main-nav-avatar" data-toggle-X="dropdown">
              <img :src="userLoginData.img" class="js-sitewide-replace-user-img site-header__user-image" width="36" height="36" alt="Profile image" />
            </a>

            <ul class="dropdown-menu site-header__nav-menu pull-right">
              <li>
                <a class="site-header__nav-user-profile" href="/user/profile">
                  <img
                    :src="userLoginData.img"
                    class="js-sitewide-replace-user-img site-header__nav-user-profile-image"
                    width="36"
                    height="36"
                    alt="Profile image"
                  />

                  <div>
                    <div class="js-sitewide-replace-user-name site-header__nav-user-profile-name">My Account</div>
                    <div class="site-header__nav-user-profile-meta">View profile</div>
                  </div>
                </a>
              </li>
              <li>
                <a href="/user/my-activity" :class="{ 'site-header__nav-menu-item': true, 'site-header__nav-menu-item--active': path == '/user/my-activity' }">
                  <div class="site-header__nav-menu-item-heading">My activity</div>
                </a>
              </li>
              <li class="site-header__nav-menu-item--has-dropdown">
                <a
                  @click.prevent="toggleCampaignsDropdown"
                  :class="{
                    'site-header__nav-menu-item': true,
                    'site-header__nav-menu-item--active': path == '/user/campaigns',
                  }"
                >
                  <div class="site-header__nav-menu-item-heading">
                    My Campaigns
                    <i
                      class="fas fa-chevron-down site-header__nav-menu-item-chevron"
                      :class="{ 'site-header__nav-menu-item-chevron--open': isCampaignsDropdownOpen }"
                    ></i>
                  </div>
                </a>
                <ul v-show="isCampaignsDropdownOpen" class="site-header__nav-menu-dropdown">
                  <template v-if="userCampaignList.length > 0">
                    <li v-for="campaign in userCampaignList" :key="campaign.id">
                      <a :href="campaign.permalink" class="site-header__nav-menu-item site-header__nav-menu-item--sub" @click="toggleCampaignsDropdown">
                        <div class="site-header__nav-menu-item-heading">{{ campaign.title }}</div>
                      </a>
                    </li>
                  </template>
                  <li v-else>
                    <a href="/user/campaigns/create" class="site-header__nav-menu-item site-header__nav-menu-item--sub" @click="toggleCampaignsDropdown">
                      <div class="site-header__nav-menu-item-heading">Start a Campaign</div>
                    </a>
                  </li>
                </ul>
              </li>
              <li>
                <a
                  href="/user/settings"
                  :class="{
                    'site-header__nav-menu-item': true,
                    'site-header__nav-menu-item--active': path.startsWith('/user/settings') || path.startsWith('/user/emails'),
                  }"
                >
                  <div class="site-header__nav-menu-item-heading">Settings</div>
                </a>
              </li>
              <li>
                <form method="POST" action="/user/logout">
                  <button @click="handleLogout" type="submit" class="inline-button site-header__nav-menu-item">
                    <div class="site-header__nav-menu-item-heading">Sign out</div>
                  </button>
                </form>
              </li>
            </ul>
          </div>
        </div>
      </header>

      <div v-if="!showCampaignEditorStyleHeader" class="js-main-nav-mobile-parent"></div>

      <!-- mobile nav -->
      <div v-if="!showCampaignEditorStyleHeader" class="mobile-menu-shadow shadow-left" id="js-mobile-nav-shadow"></div>
      <transition name="fade">
        <div v-if="!showCampaignEditorStyleHeader && isOpenhamburguerMenu" class="mobile-menu-backdrop" @click="isOpenhamburguerMenu = false"></div>
      </transition>
      <transition name="slide-fade">
        <div v-if="!showCampaignEditorStyleHeader && isOpenhamburguerMenu" class="mobile-menu-overlay">
          <div class="mobile-nav__top">
            <div class="mobile-nav__top-header">
              <i class="fal fa-bars fa-1-5x primary-1-color cursor-pointer" @click="handleHamburguerMenu"></i>
              <a href="/feed" class="site-header__main-nav-item site-header__main-nav-logo">
                <img src="https://assets.crowdblue.com/v2/production/all/img/crowdblue-logo.png" class="site-header__logo" alt="CrowdBlue logo" width="124" />
              </a>
            </div>
            <div style="max-width: 300px">
              <CpSidebarMenuGlobal :withMyRepresentatives="false" />
            </div>
            <div>
              <CpInviteMembersDialog
                :url="`${appUrl}/feed`"
                :title="
                  !!userLoginData
                    ? `${userLoginData.first_name} ${userLoginData.last_name} wants you to join CrowdBlue!`
                    : `You have been invited to join CrowdBlue!`
                "
                :description="
                  !!userLoginData
                    ? `${userLoginData.first_name} ${userLoginData.last_name} wants you to join CrowdBlue!`
                    : `You have been invited to join CrowdBlue!`
                "
                quote
                twitter-user="crowdblue"
                hashtags="crowdBlue,politics,political crowdfunding"
                :inviting-user-id="!!userLoginData ? `${userLoginData.id}` : '0'"
              />
            </div>
          </div>
        </div>
      </transition>

      <!-- mobile add menu -->
      <div v-if="!showCampaignEditorStyleHeader" class="mobile-menu-shadow shadow-right" id="js-mobile-add-shadow"></div>
      <cp-header-menu-tooltip v-if="!showCampaignEditorStyleHeader" class="start-items" :items="startItems" title="Start" />
      <cp-site-search v-if="!showCampaignEditorStyleHeader" />
    </div>
    <div v-if="isCampaignSection && showCampaignBanner" class="campaignBanner" id="campaignBanner">
      <i class="fal fa-times-circle fa-1-5x black-color campaignBanner__icon" @click="handleCloseCampaignBanner"></i>
      <span class="hidden-mobile"><i class="fal fa-podium secondary-2-color"></i></span>
      <h4 class="subhead secondary-2-color hidden-mobile" v-margin-right="0.5" v-margin="0">
        {{ userHasCreatedCampaigns ? 'View and manage your campaigns' : 'Build power in your community, create a campaign that matters!' }}
      </h4>
      <div class="campaignBanner__buttons">
        <CpButtonLink
          primary
          openExternally
          thin
          :href="userHasCreatedCampaigns ? '/user/campaigns' : '/user/campaigns/create'"
          style="background-color: var(--secondary-2); color: var(--white); padding: 0px !important"
          >{{ userHasCreatedCampaigns ? 'Go to my campaigns' : 'Start a campaign' }}</CpButtonLink
        >
        <span class="hidden-mobile">
          <CpButtonLink
            v-if="!isUserLogin || !userHasCreatedCampaigns"
            openExternally
            thin
            href="/campaigns/learn"
            style="border: 1px solid var(--secondary-2) !important; color: var(--secondary-2); background-color: transparent; padding: 0px !important"
            >Learn More</CpButtonLink
          >
        </span>
      </div>
    </div>
    <CpGoogleOneTap v-if="showGoogleOneTap && !isUserLogin" />
  </div>
</template>

<style scoped>
.banner {
  display: block;
  text-align: center;
  padding: 9px;
  background: var(--white);
  color: var(--primary);
  font-family: 'proxima-nova';
  font-size: 16px;
  opacity: 1;
}
.campaignBanner {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  background-color: var(--white);
  border-bottom: 1px solid var(--white);
  padding: 14px 0;
  gap: 8px;
}
.campaignBanner__icon {
  position: absolute;
  top: 4px;
  right: 10px;
  cursor: pointer;
}
.campaignBanner__buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}
.dropdown-menu.site-header__nav-menu {
  width: 100% !important;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}
.site-header__nav-menu-item--has-dropdown {
  position: relative;
  cursor: pointer;
}
.site-header__nav-menu-item-chevron {
  margin-left: 8px;
  font-size: 10px;
  transition: transform 0.2s ease;
}
.site-header__nav-menu-item-chevron--open {
  transform: rotate(180deg);
}
.site-header__nav-menu-dropdown {
  list-style: none;
  padding: 0;
  margin: 0;
  background-color: var(--white);
  border-top: 1px solid var(--neutral-1);
  width: 100%;
  box-sizing: border-box;
}
.site-header__nav-menu-dropdown > li {
  margin: 0;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
}
.site-header__nav-menu-item--sub {
  display: block !important;
  width: 100% !important;
  padding-left: 22px !important;
  margin: 0 !important;
  border-bottom: none !important;
  box-sizing: border-box !important;
}
.site-header__nav-menu-item--sub .site-header__nav-menu-item-heading {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.site-header__nav-menu-item--sub:hover {
  background-color: var(--neutral-1) !important;
}
.site-header__nav-menu-dropdown > li:last-child > .site-header__nav-menu-item--sub {
  border-bottom: 1px solid var(--neutral-1) !important;
}
.visible-mobile {
  display: none;
}
.hidden-mobile {
  display: inherit;
}
.hidden-small-mobile {
  display: inherit;
}
.site-header__campaign-editor {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 20px;
}
.site-header__campaign-editor-back {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  padding: 8px;
  transition: opacity 0.2s;
}
.site-header__campaign-editor-back:hover {
  opacity: 0.7;
}
@media screen and (max-width: 1050px) {
  .hide-start-campaign {
    display: none;
  }
}
@media screen and (max-width: 768px) {
  .visible-mobile {
    display: inherit;
  }
  .hidden-mobile {
    display: none;
  }
  .site-header__campaign-editor {
    padding: 0 12px;
    gap: 12px;
  }
}
@media screen and (max-width: 500px) {
  .campaignBanner {
    flex-direction: column;
    padding: 14px 43px;
  }
  .campaignBanner h4 {
    text-align: center;
    padding-bottom: 7px;
  }
  .campaignBanner__buttons {
    flex-direction: column;
  }
  .hidden-small-mobile {
    display: none;
  }
}
</style>
